// Story Text-to-Speech using the browser's native SpeechSynthesis API.
// Zero external dependencies, no API key. Quality varies by device:
// - iOS Safari: "Enhanced" / "Premium" voices sound near-Siri (good)
// - Android Chrome: Google voices are very decent
// - Desktop Chrome: Google + system voices, generally smooth
// - Older browsers / Linux: may sound robotic (the unavoidable baseline)
//
// Voice type filter: the API exposes only `name` + `lang`, no gender hint, so
// we use heuristics on the name (Maria/Lucia → female, Carlo/Marco → male).
// "Neutro" picks any non-matching voice in the right language.

const STORAGE_KEY = "ac_tts_voice_type";

// Heuristics — these patterns are NOT exhaustive but cover the common voices
// across iOS/Android/Chrome/Edge for IT and EN.
const FEMALE_PATTERNS = [
  /female/i, /donna/i,
  /\b(alice|alva|amelie|anna|allison|aria|audrey|aurora|barbara|carmen|catherine|chiara|clara|claudia|elena|elisa|elsa|elvira|emma|federica|fiona|francesca|giulia|helena|isabella|joanna|julia|karen|kate|laura|lia|lilian|lisa|lucia|luciana|maria|marta|martina|melina|mia|michelle|monica|nina|nora|paola|paulina|petra|rachel|samantha|sara|serena|silvia|sofia|sonia|stefania|susan|tania|tessa|valentina|vanessa|veronica|victoria|virginia|yelda|yuna|zosia|zuzana)\b/i,
];
const MALE_PATTERNS = [
  /male/i, /uomo/i,
  /\b(adam|alex|antonio|arthur|brian|bruce|carlo|cosimo|daniel|dario|david|diego|eddy|enrique|fabio|federico|fernando|francesco|gabriel|george|giorgio|giovanni|james|jorge|josé|juan|leonardo|lorenzo|luca|luciano|marco|mario|matteo|maurice|michael|nicola|paolo|peter|philip|piotr|riccardo|roberto|rocco|rodrigo|salvatore|sergio|simone|stefan|thomas|tom|tony|vincenzo|wayne|william|xander)\b/i,
];

function classifyVoice(voice) {
  const name = voice.name || "";
  if (FEMALE_PATTERNS.some((p) => p.test(name))) return "female";
  if (MALE_PATTERNS.some((p) => p.test(name))) return "male";
  return "neutro";
}

let cachedVoices = null;
function _loadVoices() {
  if (typeof window === "undefined" || !window.speechSynthesis) return [];
  cachedVoices = window.speechSynthesis.getVoices() || [];
  return cachedVoices;
}

export function isSupported() {
  return typeof window !== "undefined"
    && "speechSynthesis" in window
    && "SpeechSynthesisUtterance" in window;
}

export function getStoredVoiceType() {
  try { return localStorage.getItem(STORAGE_KEY) || "auto"; } catch { return "auto"; }
}
export function setStoredVoiceType(value) {
  try { localStorage.setItem(STORAGE_KEY, value); } catch { /* noop */ }
}

/**
 * Pick the best voice for the desired type + language.
 * type: "auto" | "male" | "female" | "neutro"
 * lang: "it" | "en" (or full BCP-47)
 */
export function pickVoice(type, lang) {
  const voices = _loadVoices();
  if (!voices.length) return null;
  const want = (lang || "en").toLowerCase().slice(0, 2);
  const sameLang = voices.filter((v) => (v.lang || "").toLowerCase().slice(0, 2) === want);
  const pool = sameLang.length ? sameLang : voices;

  if (type === "auto") {
    // Prefer enhanced/premium quality if hinted in name (iOS Safari)
    const enhanced = pool.find((v) => /enhanced|premium|natural|neural/i.test(v.name));
    return enhanced || pool[0] || null;
  }

  // Filter by gender heuristic
  const filtered = pool.filter((v) => classifyVoice(v) === type);
  if (filtered.length) {
    const enhanced = filtered.find((v) => /enhanced|premium|natural|neural/i.test(v.name));
    return enhanced || filtered[0];
  }
  // Fallback: any voice in the language
  return pool[0] || null;
}

let currentUtterance = null;

/** Cancel any in-flight speech (synchronous). */
export function stop() {
  try {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  } catch { /* noop */ }
  currentUtterance = null;
}

/**
 * Speak the given text. Returns a Promise that resolves when finished or
 * rejects on error. `onBoundary` (optional) fires for word/sentence highlights.
 */
export function speak(text, { lang = "en", type = "auto", rate = 0.95, pitch = 1.0, onStart, onEnd, onError } = {}) {
  return new Promise((resolve, reject) => {
    if (!isSupported() || !text) {
      reject(new Error("TTS not supported or empty text"));
      return;
    }
    stop(); // cancel any previous
    const u = new SpeechSynthesisUtterance(text);
    const voice = pickVoice(type, lang);
    if (voice) u.voice = voice;
    u.lang = (voice && voice.lang) || lang;
    u.rate = rate;
    u.pitch = pitch;
    u.onstart = () => { onStart?.(); };
    u.onend = () => { currentUtterance = null; onEnd?.(); resolve(); };
    u.onerror = (e) => { currentUtterance = null; onError?.(e); reject(e); };
    currentUtterance = u;
    try {
      window.speechSynthesis.speak(u);
    } catch (e) {
      reject(e);
    }
  });
}

export function pause() {
  try { window.speechSynthesis.pause(); } catch { /* noop */ }
}
export function resume() {
  try { window.speechSynthesis.resume(); } catch { /* noop */ }
}
export function isSpeaking() {
  try { return window.speechSynthesis?.speaking || false; } catch { return false; }
}
export function isPaused() {
  try { return window.speechSynthesis?.paused || false; } catch { return false; }
}

/**
 * Wait for the voice list to be populated. Some browsers (Chrome) populate
 * it asynchronously via the "voiceschanged" event. Returns immediately if
 * voices are already known.
 */
export function ensureVoicesLoaded(timeoutMs = 1500) {
  return new Promise((resolve) => {
    if (!isSupported()) { resolve([]); return; }
    const initial = _loadVoices();
    if (initial.length) { resolve(initial); return; }
    const handle = setTimeout(() => { resolve(_loadVoices()); }, timeoutMs);
    const handler = () => {
      clearTimeout(handle);
      window.speechSynthesis.removeEventListener("voiceschanged", handler);
      resolve(_loadVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", handler);
  });
}

/**
 * Returns a summary of voices grouped by classification, useful for the
 * Settings panel preview ("3 femminili, 5 maschili, 2 neutre").
 */
export function listVoicesSummary(lang) {
  const voices = _loadVoices();
  const want = (lang || "en").toLowerCase().slice(0, 2);
  const sameLang = voices.filter((v) => (v.lang || "").toLowerCase().slice(0, 2) === want);
  const pool = sameLang.length ? sameLang : voices;
  const byType = { female: 0, male: 0, neutro: 0 };
  for (const v of pool) byType[classifyVoice(v)]++;
  return { total: pool.length, ...byType };
}
